<template>
  <modal v-if="showInvite" @close="$emit('cancel')">
    <h2 slot="header">
      {{ isBulkInvite ? `Invite ${selectedCount} Investors` : `Invite ${profileData.full_name || (profileData.first_name + ' ' + profileData.last_name)}` }}
    </h2>
    <div slot="body">
      <!-- Displaying User Info or Bulk Info -->
      <div v-if="isBulkInvite" class="alert alert-info">
        <strong>Bulk Invite:</strong> You are about to send invites to {{ selectedCount }} selected investors.
      </div>
      <div v-else>
        <label>User Name: {{ profileData.full_name || (profileData.first_name + " " + profileData.last_name) }}</label>
        <label>User Email: {{ profileData.email }}</label>
        <br/><br/>
      </div>
      
      <!-- Invite to Offering or Document Flow -->
      <label>Invite to:</label>
      <div class="radio radio-primary">
        <input type="radio" name="Invite_to" class="radio-primary" value="offering" v-model="inviteTo" @change="isVisible"/>
        <label>Offering</label>
      </div>
      <div class="radio radio-primary">
        <input type="radio" name="Invite_to" class="radio-primary" value="docflow" v-model="inviteTo" @change="isVisible"/>
        <label>Document Flow</label>
      </div>

      <!-- Showing Offering Dropdown if 'Offering' is selected -->
      <div v-if="inviteTo === 'offering'" class="form-group">
        <label for="investor_offering">Select an Offering</label>
        
        <div v-if="loadingOfferings" class="text-center">
          <i class="fa fa-spinner fa-spin"></i> Loading offerings...
        </div>
        
        <select
          v-else
          id="investor_offering"
          class="form-control"
          name="investor_offering"
          v-model="selectedOffering"
          required
        >
          <option disabled="disabled" value="">--Select an Offering--</option>
          <option v-for="option in offeringList" :value="option" :key="option.id">
            {{ option.name }}
          </option>
        </select>
      </div>

      <!-- Showing Document Flow Dropdown if 'Document Flow' is selected -->
      <div v-if="inviteTo === 'docflow'" class="form-group">
        <label for="investor_docflow">Select a Document Flow</label>
        
        <div v-if="loadingDocFlows" class="text-center">
          <i class="fa fa-spinner fa-spin"></i> Loading document flows...
        </div>
        
        <select
          v-else
          id="investor_docflow"
          class="form-control"
          name="investor_docflow"
          v-model="selectedDocFlow"
          required
        >
          <option disabled="disabled" value="">--Select a Document Flow--</option>
          <option v-for="option in docFlows" :value="option" :key="option.id">
            {{ option.name }}
          </option>
        </select>
      </div>
    </div>
    <div slot="footer">
      <!-- Cancel Button -->
      <button
        class="modal-default-button btn btn-default margin-left-10"
        type="button"
        @click="cancel"
      >
        Cancel
      </button>
      
      <!-- Invite Button -->
      <button
        class="modal-default-button btn btn-primary"
        type="button"
        @click="inviteInvestor"
        :disabled="!canSendInvite || sending"
      >
        {{ sending ? 'Sending...' : (isBulkInvite ? `Invite ${selectedCount} Investors` : 'Invite') }}
      </button>

      <!-- Response Message -->
      <label v-if="showResMessage">{{ resMessage }}</label>
    </div>
  </modal>
</template>

<script>
import Modal from '../Modal.vue'
import axios from "axios";

export default {
  name: "InviteInvestorModal",
  components: {
    Modal,
  },
  props: {
    showInvite: Boolean,
    profileData: {
      type: Object,
      default: () => ({
        id: 0,
        first_name: '',
        last_name: '',
        email: '',
        selectedInvestors: []
      })
    },
    offeringList: {
      type: Array,
      default: () => []
    },
    docFlows: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isBulkInvite() {
      return this.profileData.selectedInvestors && this.profileData.selectedInvestors.length > 1;
    },
    selectedCount() {
      return this.profileData.selectedInvestors ? this.profileData.selectedInvestors.length : 1;
    },
    canSendInvite() {
      if (this.inviteTo === 'offering') {
        return this.selectedOffering && this.selectedOffering.id;
      } else if (this.inviteTo === 'docflow') {
        return this.selectedDocFlow && this.selectedDocFlow.id;
      }
      return false;
    }
  },
  data() {
    return {
      loadingOfferings: false,
      loadingDocFlows: false,
      selectedOffering: '',
      selectedDocFlow: '',
      resMessage: '',
      showResMessage: false,
      inviteTo: 'offering',
      sending: false
    }
  },
  methods: {
    inviteInvestor() {
      if (this.inviteTo === 'offering') {
        this.inviteToOffering();
      } else if (this.inviteTo === 'docflow') {
        this.inviteToDocumentFlow();
      }
    },
    
    async inviteToOffering() {
      if (!this.selectedOffering || !this.selectedOffering.id) {
        alert('Please select an offering');
        return;
      }

      this.sending = true;
      const investorIds = this.profileData.selectedInvestors || [this.profileData.id];
      const promises = [];

      try {
        for (const investorId of investorIds) {
          const payload = {
            investor_id: investorId,
            offering_id: this.selectedOffering.id
          };
          
          promises.push(axios.post('investor/invite', payload));
        }

        const responses = await Promise.all(promises);
        const allSuccessful = responses.every(response => response.data.status === 'success');

        if (allSuccessful) {
          this.resMessage = `Successfully sent ${investorIds.length} offering invite${investorIds.length > 1 ? 's' : ''}`;
          this.showResMessage = true;
          this.timer();
        } else {
          this.resMessage = 'Some invites failed to send. Please try again.';
          this.showResMessage = true;
        }
      } catch (error) {
        console.error('Error sending invites:', error);
        this.resMessage = 'An error occurred while sending invites.';
        this.showResMessage = true;
      } finally {
        this.sending = false;
      }
    },

    async inviteToDocumentFlow() {
      if (!this.selectedDocFlow || !this.selectedDocFlow.id) {
        alert('Please select a document flow');
        return;
      }

      this.sending = true;
      const investorIds = this.profileData.selectedInvestors || [this.profileData.id];
      const promises = [];

      try {
        for (const investorId of investorIds) {
          const payload = {
            investor_id: investorId,
            doc_flow_id: this.selectedDocFlow.id
          };
          
          promises.push(axios.post('investor/flow_invite', payload));
        }

        const responses = await Promise.all(promises);
        const allSuccessful = responses.every(response => response.data.status === 'success');

        if (allSuccessful) {
          this.resMessage = `Successfully sent ${investorIds.length} document flow invite${investorIds.length > 1 ? 's' : ''}`;
          this.showResMessage = true;
          this.timer();
        } else {
          this.resMessage = 'Some invites failed to send. Please try again.';
          this.showResMessage = true;
        }
      } catch (error) {
        console.error('Error sending invites:', error);
        this.resMessage = 'An error occurred while sending invites.';
        this.showResMessage = true;
      } finally {
        this.sending = false;
      }
    },

    cancel() {
      this.showResMessage = false;
      this.resMessage = '';
      this.selectedOffering = '';
      this.selectedDocFlow = '';
      this.$emit('cancel');
    },

    timer() {
      setTimeout(() => {
        this.showResMessage = false;
        this.resMessage = '';
        this.$emit('ok');
      }, 3000);
    },

    isVisible() {
      // Reset selections when switching between invite types
      this.selectedOffering = '';
      this.selectedDocFlow = '';
    },
  },

  created() {
    this.inviteTo = 'offering';
  }
}
</script>